import { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useRecommendationPolling } from './useRecommendationPolling';
import type { HomeRecommendationsResponse } from '../types/recommendation.types';

interface UseHomeRecommendationsParams {
  localHour: number;
  ageRanges?: string;
  timezone?: string;
}

interface UseHomeRecommendationsResult {
  data: HomeRecommendationsResponse | null;
  isLoading: boolean;
  isRegenerating: boolean;
  error: string | null;
  refetch: () => void;
}

export function useHomeRecommendations({
  localHour,
  ageRanges,
  timezone,
}: UseHomeRecommendationsParams): UseHomeRecommendationsResult {
  const [data, setData] = useState<HomeRecommendationsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecommendations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await api.getHomeRecommendations(localHour, ageRanges, timezone);

      setData(result);
      setIsRegenerating(result.isRegenerating);
      setIsLoading(false);
    } catch (err: unknown) {
      console.error('[useHomeRecommendations] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch recommendations');
      setIsLoading(false);
    }
  }, [localHour, ageRanges, timezone]);

  const pollStatus = useCallback(async () => {
    try {
      const status = await api.getHomeRecommendationStatus();

      if (status.status === 'completed' && !status.hasInProgressJob) {
        setIsRegenerating(false);
        await fetchRecommendations();
      } else if (status.status === 'not_started') {
        setIsRegenerating(false);
      }
    } catch (err) {
      console.debug('[useHomeRecommendations] Polling error:', err);
    }
  }, [fetchRecommendations]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  useRecommendationPolling(isRegenerating, pollStatus);

  return {
    data,
    isLoading,
    isRegenerating,
    error,
    refetch: fetchRecommendations,
  };
}
